import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Clock3, RefreshCcw, AlertCircle } from 'lucide-react';

const SLOT_DURATION_MINUTES = 30;
const CAL_USERNAME = (process.env.REACT_APP_CAL_USERNAME || '').trim();
const CAL_EVENT_TYPE = (process.env.REACT_APP_CAL_EVENT_TYPE || '').trim();

const trimTrailingSlash = (value = '') => (value ? value.replace(/\/+$/, '') : '');

const formatDateForApi = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthRange = (referenceDate = new Date()) => {
  const base =
    referenceDate instanceof Date
      ? referenceDate
      : new Date(referenceDate || new Date());
  const validBase = Number.isNaN(base.getTime()) ? new Date() : base;
  const start = new Date(validBase.getFullYear(), validBase.getMonth(), 1);
  const end = new Date(validBase.getFullYear(), validBase.getMonth() + 1, 0);
  return { start: formatDateForApi(start), end: formatDateForApi(end) };
};

const formatDateLabel = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
};

const formatMonthTitle = (dateKey) => {
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
};

const formatTimeLabel = (isoString, timeZone) => {
  if (!isoString) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone || 'America/Chicago'
    }).format(new Date(isoString));
  } catch (error) {
    console.warn('Unable to format time', isoString, error);
    return '';
  }
};

const addMinutes = (date, minutes) => {
  const clone = new Date(date);
  clone.setMinutes(clone.getMinutes() + minutes);
  return clone;
};

const coerceDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeSlotEntry = (entry) => {
  if (!entry) return null;

  const startRaw = typeof entry === 'string' || typeof entry === 'number' ? entry : entry.startTime || entry.start || entry.time || entry.slot;
  const endRaw = typeof entry === 'object' ? entry.endTime || entry.end : null;
  const timeZone = typeof entry === 'object' ? entry.timeZone || entry.timezone || entry.tz || null : null;

  const startDate = coerceDate(startRaw);
  if (!startDate) return null;

  const endDate = coerceDate(endRaw) || addMinutes(startDate, SLOT_DURATION_MINUTES);

  return {
    start: startDate.toISOString(),
    end: endDate ? endDate.toISOString() : null,
    timeZone,
    uid: (typeof entry === 'object' && (entry.uid || entry.id || entry.slotId)) || null
  };
};

const normalizeSlotsPayload = (payload) => {
  const normalized = {};
  const rawSlots = payload?.data?.slots ?? payload?.slots ?? payload?.data ?? [];

  const addSlot = (slot, dateKeyOverride) => {
    const parsed = normalizeSlotEntry(slot);
    if (!parsed) return;
    const dateKey = dateKeyOverride || parsed.start.slice(0, 10);
    parsed.timeZone = parsed.timeZone || 'America/Chicago';
    if (!normalized[dateKey]) normalized[dateKey] = [];
    normalized[dateKey].push(parsed);
  };

  if (Array.isArray(rawSlots)) {
    rawSlots.forEach((slot) => addSlot(slot));
  } else if (rawSlots && typeof rawSlots === 'object') {
    Object.entries(rawSlots).forEach(([dateKey, slotList]) => {
      if (Array.isArray(slotList)) {
        slotList.forEach((slot) => addSlot(slot, dateKey));
      } else {
        addSlot(slotList, dateKey);
      }
    });
  }

  Object.keys(normalized).forEach((key) => {
    normalized[key].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  });

  return normalized;
};

const buildBookingId = (slot, dateKey) => {
  if (slot?.uid) return `slot-${slot.uid}`;
  if (slot?.id) return `slot-${slot.id}`;
  const timestamp = slot?.start ? new Date(slot.start).getTime() : Date.now();
  const safeStamp = Number.isFinite(timestamp) ? timestamp : Date.now();
  return `slot-${dateKey || 'selection'}-${safeStamp}`;
};

const persistSessionSelection = (selection) => {
  if (typeof window === 'undefined') return;
  try {
    if (!selection) {
      window.sessionStorage.removeItem('calSelection');
      return;
    }
    window.sessionStorage.setItem('calSelection', JSON.stringify(selection));
  } catch (error) {
    console.warn('Unable to persist selection', error);
  }
};

const readSessionSelection = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem('calSelection');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.dateKey && parsed?.slot?.start) {
      return parsed;
    }
  } catch (error) {
    console.warn('Unable to read selection', error);
  }
  return null;
};

export default function DatePicker({
  service,
  size,
  rooms,
  baths,
  extras = [],
  total,
  entryInfo = {},
  requests = '',
  onBookingComplete,
  onSelectionChange,
  disabled = false,
  highlightMissing = false
}) {
  const todayMonthStart = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);
  const maxMonthStart = useMemo(
    () => new Date(todayMonthStart.getFullYear(), todayMonthStart.getMonth() + 1, 1),
    [todayMonthStart]
  );

  const [monthCursor, setMonthCursor] = useState(() => new Date(todayMonthStart));
  const [slotsByDate, setSlotsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirmedSelection, setConfirmedSelection] = useState(null);
  const [selectionByDate, setSelectionByDate] = useState({});
  const [slotsError, setSlotsError] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const refreshTimeoutRef = useRef(null);

  const apiBase = useMemo(() => {
    const envBase =
      trimTrailingSlash(process.env.REACT_APP_CAL_API_BASE) ||
      trimTrailingSlash(process.env.REACT_APP_WEEKLY_API_BASE) ||
      trimTrailingSlash(process.env.REACT_APP_API_URL || '');
    if (envBase) {
      return envBase;
    }

    if (typeof window !== 'undefined') {
      const { protocol, hostname, port } = window.location;
      let effectivePort = '';
      if (port && port !== '80' && port !== '443') {
        effectivePort = port === '3000' ? '5000' : port;
      }
      const hostWithPort = effectivePort ? `${hostname}:${effectivePort}` : hostname;
      return `${protocol}//${hostWithPort}`;
    }

    return '';
  }, []);

  const monthRange = useMemo(() => getMonthRange(monthCursor), [monthCursor]);

  const availableDates = useMemo(
    () => Object.keys(slotsByDate).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
    [slotsByDate]
  );

  const bookingContextLine = useMemo(() => {
    const serviceLabel = service === 'deep' ? 'Deep cleaning' : 'Basic cleaning';
    const sizeLabel = `${Math.round(size || 0).toLocaleString()} sq ft`;
    const roomLabel = rooms === 'Studio' ? 'Studio' : `${rooms} rooms`;
    const bathLabel = baths ? `${baths} bath${String(baths) === '1' ? '' : 's'}` : '';
    const extrasLabel =
      extras?.length > 0 ? `${extras.length} add-on${extras.length > 1 ? 's' : ''}` : 'No add-ons';
    return [serviceLabel, sizeLabel, roomLabel, bathLabel, extrasLabel].filter(Boolean).join(' • ');
  }, [service, size, rooms, baths, extras]);

  const monthTitle = useMemo(() => {
    const targetDate = selectedDate || monthRange.start || '';
    return formatMonthTitle(targetDate);
  }, [selectedDate, monthRange.start]);

  const monthDate = useMemo(() => {
    if (monthRange.start) {
      const date = new Date(`${monthRange.start}T00:00:00`);
      if (!Number.isNaN(date.getTime())) return date;
    }
    return new Date();
  }, [monthRange.start]);

  const monthGrid = useMemo(() => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = new Date(year, month, 1).getDay();

    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month, day);
      const dateKey = formatDateForApi(dateObj);
      const hasSlots = Boolean(slotsByDate[dateKey]?.length);
      cells.push({
        day,
        dateKey,
        hasSlots,
        isSelected: selectedDate === dateKey
      });
    }

    return cells;
  }, [monthDate, selectedDate, slotsByDate]);

  const selectedDateSlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  const todayKey = useMemo(() => formatDateForApi(new Date()), []);

  const loadSlots = useCallback(async () => {
    if (!CAL_USERNAME || !CAL_EVENT_TYPE) {
      setSlotsError('Scheduling is not configured. Missing CAL env values.');
      return;
    }
    if (!apiBase) {
      setSlotsError('Unable to reach the scheduling server.');
      return;
    }
    setLoadingSlots(true);
    setSlotsError('');

    try {
      const params = new URLSearchParams({
        start: monthRange.start,
        end: monthRange.end,
        username: CAL_USERNAME,
        eventTypeSlug: CAL_EVENT_TYPE
      });
      const response = await fetch(`${apiBase}/api/cal/slots?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Unable to load available times right now.');
      }

      const payload = await response.json().catch(() => ({}));
      const normalized = normalizeSlotsPayload(payload);
      setSlotsByDate(normalized);

      const nextDate = Object.keys(normalized).sort((a, b) => new Date(a) - new Date(b))[0] || '';
      setSelectedDate((prev) => prev || nextDate);
      setSelectedSlot((prev) => (prev && nextDate === selectedDate ? prev : null));
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setSlotsError(error.message || 'Unable to load available times right now.');
    } finally {
      setLoadingSlots(false);
    }
  }, [apiBase, monthRange.end, monthRange.start, selectedDate]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    const restored = readSessionSelection();
    if (restored?.dateKey && restored?.slot) {
      setSelectedDate(restored.dateKey);
      setSelectionByDate({ [restored.dateKey]: restored.slot });
      setConfirmedSelection(restored);
      setSelectedSlot(restored.slot);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) return;
    if (!availableDates.length) return;
    setSelectedDate(availableDates[0]);
  }, [availableDates, selectedDate]);

  useEffect(
    () => () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    },
    []
  );

  const handleDateSelect = (dateKey) => {
    if (!dateKey) return;
    setSelectedDate(dateKey);
    setSelectedSlot(selectionByDate[dateKey] || null);
  };

  const canGoNext = useMemo(() => {
    const current = coerceDate(monthCursor) || todayMonthStart;
    return current.getTime() < maxMonthStart.getTime();
  }, [maxMonthStart, monthCursor, todayMonthStart]);

  const canGoPrev = useMemo(() => {
    const current = coerceDate(monthCursor) || todayMonthStart;
    return current.getTime() > todayMonthStart.getTime();
  }, [monthCursor, todayMonthStart]);

  // Restore a previously chosen slot when returning to its date
  useEffect(() => {
    if (!selectedDate) return;
    const storedSlot = selectionByDate[selectedDate];
    if (!storedSlot) {
      if (selectedSlot) setSelectedSlot(null);
      return;
    }
    const availableMatch =
      slotsByDate[selectedDate]?.find((slot) => slot.start === storedSlot.start) || storedSlot;
    if (!selectedSlot || selectedSlot.start !== availableMatch.start) {
      setSelectedSlot(availableMatch);
    }
  }, [selectedDate, selectionByDate, slotsByDate, selectedSlot]);

  // Keep the persisted summary selection in sync with refreshed slot data
  useEffect(() => {
    if (!confirmedSelection?.slot || !confirmedSelection?.dateKey) return;
    const { dateKey, slot } = confirmedSelection;
    const availableMatch = slotsByDate[dateKey]?.find((entry) => entry.start === slot.start);
    if (availableMatch && availableMatch !== slot) {
      setConfirmedSelection({ dateKey, slot: availableMatch });
      if (selectedDate === dateKey && (!selectedSlot || selectedSlot.start === slot.start)) {
        setSelectedSlot(availableMatch);
      }
      setSelectionByDate({ [dateKey]: availableMatch });
    }
  }, [confirmedSelection, selectedDate, selectedSlot, slotsByDate]);

  const lastAnnouncedSelection = useRef(null);
  useEffect(() => {
    if (confirmedSelection === lastAnnouncedSelection.current) return;
    // Avoid announcing an empty selection on initial load
    if (!confirmedSelection && !lastAnnouncedSelection.current) return;
    onSelectionChange?.(confirmedSelection);
    persistSessionSelection(confirmedSelection);
    lastAnnouncedSelection.current = confirmedSelection;
  }, [confirmedSelection, onSelectionChange]);

  const shiftMonth = (delta) => {
    const base = coerceDate(monthCursor) || new Date();
    const candidate = new Date(base.getFullYear(), base.getMonth() + delta, 1);
    if (delta > 0 && candidate.getTime() > maxMonthStart.getTime()) return;
    if (candidate.getTime() === base.getTime()) return;

    setMonthCursor(candidate);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlotsByDate({});
  };

  const handlePrevMonth = () => shiftMonth(-1);
  const handleNextMonth = () => shiftMonth(1);

  const handleSlotSelect = (slot) => {
    if (!slot || disabled) return;
    setSelectedSlot(slot);
    if (selectedDate) {
      setSelectionByDate({ [selectedDate]: slot });
      setConfirmedSelection({ dateKey: selectedDate, slot });
    }

    const bookingId = buildBookingId(slot, selectedDate);

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('bookingId', bookingId);
        window.sessionStorage.setItem('calBookingUid', bookingId);
        window.sessionStorage.setItem('bookingUid', bookingId);
      }
    } catch (storageError) {
      console.warn('Unable to store booking selection locally:', storageError);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      onBookingComplete?.({
        success: true,
        bookingId,
        bookingUid: bookingId,
        calBookingUid: bookingId,
        customerEmail: '',
        summary: null,
        slot: {
          start: slot.start,
          end: slot.end,
          timeZone: slot.timeZone
        },
        metadata: {
          service,
          size,
          rooms,
          baths,
          extras,
          entryInfo,
          requests,
          total
        }
      });
    }, 1800);
  };

  const calendarWrapperClass = disabled
    ? 'calendar-shell calendar-shell--disabled'
    : 'calendar-shell';
  const showMissingState = highlightMissing && !selectedSlot;
  const calendarShellClass = 'custom-calendar' + (showMissingState ? ' custom-calendar--missing' : '');

  const selectedDateLabel = selectedDate ? formatDateLabel(selectedDate) : '';
  const selectedTimeLabel = selectedSlot
    ? formatTimeLabel(selectedSlot.start, selectedSlot.timeZone)
    : '';
  const summaryDateLabel = confirmedSelection?.dateKey
    ? formatDateLabel(confirmedSelection.dateKey)
    : selectedDateLabel;
  const summaryTimeLabel = confirmedSelection?.slot
    ? formatTimeLabel(confirmedSelection.slot.start, confirmedSelection.slot.timeZone)
    : selectedTimeLabel;
  const availableSlotCount =
    selectedDate && !loadingSlots ? selectedDateSlots.length : null;
  const availabilityText =
    availableSlotCount === null
      ? ''
      : availableSlotCount > 0
        ? `${availableSlotCount} Available`
        : 'No Availability';
  const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const showAvailabilityPill = loadingSlots || Boolean(availabilityText);
  const availabilityLabel = loadingSlots ? 'Loading times...' : availabilityText;
  const availabilityPillClass =
    'calendar-availability-pill' +
    (!loadingSlots && availableSlotCount === 0 ? ' calendar-availability-pill--empty' : '');
  return (
    <div className="space-y-6">
      <div className="text-center calendar-hero">
        <div
          className="feature-badge gold mx-auto"
          style={{ background: 'linear-gradient(45deg, #bfa14a, #a68b3a)' }}
        >
          <Calendar className="w-8 h-8" style={{ color: '#ffffff' }} />
        </div>
        <h2 className="section-heading" style={{ textAlign: 'center', marginTop: '20px', marginBottom: '12px' }}>
          Schedule Your Cleaning
        </h2>
        <div className="calendar-hero__divider" />
      </div>

      <div className={`w-full ${calendarWrapperClass}`} aria-disabled={disabled}>
        <div className={calendarShellClass}>
          {slotsError ? (
            <div className="calendar-alert">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="calendar-alert__title">We hit a snag</p>
                <p className="calendar-alert__body">{slotsError}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="calendar-left">
                <div className="calendar-month-header">
                  <button
                    type="button"
                    className="calendar-nav"
                    onClick={handlePrevMonth}
                    disabled={loadingSlots || !canGoPrev}
                    aria-label="Previous month"
                  >
                    <span className="calendar-nav__icon" aria-hidden="true">‹</span>
                  </button>
                  <div className="calendar-month-title">{monthTitle}</div>
                  <button
                    type="button"
                    className="calendar-nav"
                    onClick={handleNextMonth}
                    disabled={loadingSlots || !canGoNext}
                    aria-label="Next month"
                  >
                    <span className="calendar-nav__icon" aria-hidden="true">›</span>
                  </button>
                </div>
                <div className="calendar-weekdays">
                  {weekdayLabels.map((day) => (
                    <span key={day} className="calendar-weekday">
                      {day}
                    </span>
                  ))}
                </div>
                <div className="calendar-grid">
                  {monthGrid.map((cell, index) =>
                    cell ? (
                      <button
                        key={cell.dateKey}
                        type="button"
                        className={
                          'calendar-day' +
                          (cell.hasSlots ? ' calendar-day--available' : ' calendar-day--empty') +
                          (confirmedSelection?.dateKey === cell.dateKey
                            ? ' calendar-day--selected'
                            : cell.isSelected
                              ? ' calendar-day--selected-soft'
                              : '')
                        }
                        onClick={() => cell.hasSlots && handleDateSelect(cell.dateKey)}
                        disabled={!cell.hasSlots}
                      >
                        <span aria-hidden="true">{cell.day}</span>
                        {cell.dateKey === todayKey && <span className="calendar-day__dot" />}
                      </button>
                    ) : (
                      <span key={`blank-${index}`} className="calendar-day calendar-day--blank" />
                    )
                  )}
                </div>
              </div>

              <div className="calendar-right">
                <div className="calendar-times-header">
                  <div className="calendar-times-header__main">
                    <Clock3 className="w-5 h-5" />
                    <span>{selectedDateLabel || 'Select a date to see times'}</span>
                  </div>
                  {showAvailabilityPill && (
                    <span className={availabilityPillClass}>{availabilityLabel}</span>
                  )}
                </div>

                {loadingSlots && (
                  <div
                    className="calendar-times-list"
                    aria-busy="true"
                    aria-live="polite"
                    role="status"
                  >
                    {[0, 1, 2].map((slotIndex) => (
                      <div
                        key={`skeleton-${slotIndex}`}
                        className="calendar-time-option calendar-time-option--skeleton skeleton-shimmer"
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}

                {!loadingSlots && selectedDate && !selectedDateSlots.length && (
                  <div className="calendar-placeholder">
                    No times left for this day. Choose another date.
                  </div>
                )}

                {!loadingSlots && selectedDateSlots.length > 0 && (
                  <div className="calendar-times-list">
                    {selectedDateSlots.map((slot) => {
                      const startLabel = formatTimeLabel(slot.start, slot.timeZone);
                      const endLabel = slot.end ? formatTimeLabel(slot.end, slot.timeZone) : '';
                      const isSelected = selectedSlot?.start === slot.start;
                      return (
                        <button
                          key={slot.start}
                          type="button"
                          className={
                            'calendar-time-option' +
                            (isSelected ? ' calendar-time-option--selected' : '')
                          }
                          onClick={() => handleSlotSelect(slot)}
                        >
                          {startLabel}
                          {endLabel ? ` – ${endLabel}` : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {(confirmedSelection || selectedSlot) && (
            <div className="calendar-selection">
              <div>
                <p className="calendar-selection__label">Selected</p>
                <p className="calendar-selection__value">
                  {summaryDateLabel} at {summaryTimeLabel}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
